import { logger } from "../services/logger.ts";
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import type { Market, MarketPool } from '../types.ts';
import { AlertTriangle, ArrowLeft, Info, TrendingUp, Edit3, List, CheckSquare, XSquare, Trash2, DollarSign, Loader2, Percent } from 'lucide-react';

// Import the actual components with correct paths
import { MarketStats } from '../components/admin/MarketStats.tsx';
import { MarketEditor } from '../components/admin/MarketEditor.tsx';
import { BetViewer } from '../components/admin/BetViewer.tsx';
import { MarketResolver } from '../components/admin/MarketResolver.tsx';
import { MarketCanceller } from '../components/admin/MarketCanceller.tsx';
import { MarketFeesTab } from '../components/admin/MarketFeesTab.tsx';

type Tab = 'overview' | 'edit' | 'bets' | 'fees' | 'resolve' | 'cancel' | 'delete' | 'refunds';

// Add RefundsTab component
interface BetWithRelations {
  id: string;
  user_id: string;
  market_id: string;
  market_option_id: number;
  amount: number;
  amount_after_fee?: number;
  service_fee_amount?: number;
  status: string;
  created_at: string;
  updated_at: string;
  users?: {
    email?: string;
  };
  market_options?: {
    name?: string;
  };
}

export function AdminMarketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [market, setMarket] = useState<Market | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  // Check if user is admin
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  useEffect(() => {
    // Redirect non-admin users
    if (user && !isAdmin && !loading) { // Ensure not to redirect during initial load
      logger.info('User is not admin, redirecting...');
      navigate('/markets'); // Redirect to a non-admin page
    }
  }, [user, isAdmin, loading, navigate]);

  const fetchMarketData = useCallback(async () => {
    // Ensure id is available before fetching
    if (!id || !isAdmin) {
      logger.info('Skipping fetch: ID or admin status missing.');
      // If id is missing, set error. If not admin, effects should handle redirect.
      if (!id) {
        setError('Market ID is missing.');
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);
    try {
      logger.info(`Fetching market data for ID: ${id}`);
      
      // First get the market data
      const { data, error: fetchError } = await supabase
        .from('markets')
        .select(`
          *,
          unique_traders,
          market_options!market_options_market_id_fkey(*),
          market_pools(*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) {
        logger.error('Supabase fetch error:', fetchError);
        throw fetchError;
      }
      if (!data) {
        logger.error(`Market not found for ID: ${id}`);
        throw new Error('Market not found');
      }
      
      // Then get the bet count separately
      logger.info('Fetching bet count for market ID:', id);
      const { data: countResult, error: betCountError } = await supabase
        .rpc('get_market_bet_count', { p_market_id: id });
        
      if (betCountError) {
        logger.error('Error fetching bet count:', betCountError);
        // Don't throw, continue with zero count
      }
      
      const betCount = countResult || 0;
      logger.info('Bet count from RPC:', betCount);
      logger.info('Market data fetched successfully:', data);

      const processedData: Market = {
        ...data,
        market_options: data.market_options || [],
        market_pools: data.market_pools || [],
        totalPool: (data.market_pools || []).reduce((sum: number, pool: MarketPool) => sum + (pool.total_amount || 0), 0),
        bet_count: betCount
      };
      setMarket(processedData);

    } catch (err: unknown) {
      logger.error('Error in fetchMarketData:', err);
      const message = err instanceof Error ? err.message : 'Failed to load market details';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id, isAdmin, supabase]); // Add dependencies for useCallback

  useEffect(() => {
    fetchMarketData(); // Call the memoized function
  }, [fetchMarketData]); // Dependency array includes the memoized function

  const handleMarketResolved = () => {
    logger.info('Market resolved, refreshing data...');
    fetchMarketData(); // Re-fetch market data using the memoized function
    setActiveTab('overview'); // Optionally switch back to overview tab
  };

  const handleBack = () => {
    // Get stored filters from localStorage if they exist
    const storedStatusFilter = localStorage.getItem('adminMarketsStatusFilter');
    const storedCategoryFilter = localStorage.getItem('adminMarketsCategoryFilter');
    const storedSearchTerm = localStorage.getItem('adminMarketsSearchTerm');
    
    // Build query parameters for the URL
    const params = new URLSearchParams();
    if (storedStatusFilter) params.append('status', storedStatusFilter);
    if (storedCategoryFilter) params.append('category', storedCategoryFilter);
    if (storedSearchTerm) params.append('search', storedSearchTerm);
    
    // Navigate back with filters as query parameters
    const queryString = params.toString();
    navigate(`/admin/markets${queryString ? `?${queryString}` : ''}`);
  };

  const handleMarketDeleted = () => {
    logger.info('Market deletion successful, redirecting to markets page');
    
    // Get stored filters from localStorage if they exist
    const storedStatusFilter = localStorage.getItem('adminMarketsStatusFilter');
    const storedCategoryFilter = localStorage.getItem('adminMarketsCategoryFilter');
    const storedSearchTerm = localStorage.getItem('adminMarketsSearchTerm');
    
    // Build query parameters for the URL
    const params = new URLSearchParams();
    // Add refresh parameter to force a data refresh
    const timestamp = Date.now();
    params.append('refresh', timestamp.toString());
    
    // Add filter parameters if they exist
    if (storedStatusFilter) params.append('status', storedStatusFilter);
    if (storedCategoryFilter) params.append('category', storedCategoryFilter);
    if (storedSearchTerm) params.append('search', storedSearchTerm);
    
    // Navigate back with refresh and filters as query parameters
    navigate(`/admin/markets?${params.toString()}`, { replace: true });
  };

  const renderTabContent = () => {
    // Render loading/error within the content area if tabs are already visible
    if (loading && !market) return <LoadingSpinner />;
    if (error && !market) return <ErrorMessage error={error} />;
    if (!market) return <InfoMessage message="Market data is not available." />;
    // Ensure id is present before rendering components needing it
    if (!id) return <ErrorMessage error="Market ID is missing." />;

    // Explicitly pass marketId from the checked 'id' variable
    const resolverProps = { 
      market,
      marketId: id, // Pass the guaranteed string 'id'
      onResolved: handleMarketResolved
    };

    // Check if market can be edited
    const canEdit = !['ended', 'expired', 'cancelled', 'resolved'].includes(market.status?.toLowerCase() || '');
    logger.info('Market status:', market.status);
    logger.info('Can edit:', canEdit);

    switch (activeTab) {
      case 'overview':
        // Pass totalPool and uniqueTraders specifically to MarketStats
        return <MarketStats 
                 market={market} 
                 marketId={id}
                 totalPool={market.totalPool ?? 0} 
                 uniqueTraders={market.unique_traders ?? 0} // Pass unique_traders
               />;
      case 'edit':
        return <MarketEditor 
                 market={market} 
                 marketId={id} 
                 canEdit={true} 
                 onMarketUpdate={fetchMarketData} 
               />;
      case 'bets':
        // Pass only marketId to BetViewer as it likely fetches its own bet data
        return <BetViewer marketId={id} />;
      case 'fees':
        return <MarketFeesTab market={market} marketId={id} onMarketUpdate={fetchMarketData} />;
      case 'resolve':
        // Conditionally render resolver based on market status.
        // 'ended' = trading has finished but the scheduled status-update job
        // hasn't yet flipped the row to 'expired'. Admins still need to be
        // able to resolve it.
        return (market.status === 'active' || market.status === 'ended' || market.status === 'expired') ?
          <MarketResolver {...resolverProps} />
          : <InfoMessage message="Market must be active, ended, or expired to resolve." />;
      case 'cancel':
        // Conditionally render canceller based on market status
        return (['active', 'ended', 'expired', 'cancelled'].includes(market.status?.toLowerCase() || '')) ? 
          <MarketCanceller 
            market={market} 
            marketId={id} 
            onCancel={(reason: string) => {
              logger.info(`Market cancelled with reason: ${reason}`);
              fetchMarketData();
            }}
            onRefreshMarketData={fetchMarketData}
          /> 
          : <InfoMessage message="This market cannot be cancelled in its current state." />;
      case 'delete':
        // Conditionally render market deleter
        return <MarketDeleter market={market} marketId={id} onDeleted={handleMarketDeleted} />;
      case 'refunds':
        // Conditionally render refunds tab
        return <RefundsTab market={market} marketId={id} />;
      default:
        return null;
    }
  };

  // Top-level loading/error before the main layout is rendered
  if (loading && !market) {
    return <LoadingSpinner />;
  }

  if (error && !market) {
    return <ErrorMessage error={error} onRetry={handleBack} retryLabel="Back to Markets" />;
  }

  // If loading is done, no error, but market is still null (e.g., not found after fetch)
  if (!loading && !error && !market) {
    return <ErrorMessage error="Market data could not be loaded or not found." onRetry={handleBack} retryLabel="Back to Markets" />;
  }
  
  // Need to ensure market is not null here for the TS compiler
  if (!market) return null; // Should not happen due to checks above

  // Basic structure - enhance with better styling (e.g., Shadcn UI) later
  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="mr-4 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold truncate" title={market.title}>{market.title}</h1>
        <div className="ml-4">
          <StatusBadge status={market.status} />
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex overflow-x-auto" aria-label="Tabs">
          <TabButton icon={TrendingUp} label="Overview" tabName="overview" activeTab={activeTab} setActiveTab={setActiveTab} />
          <TabButton icon={Edit3} label="Edit" tabName="edit" activeTab={activeTab} setActiveTab={setActiveTab} />
          <TabButton icon={List} label="Trades" tabName="bets" activeTab={activeTab} setActiveTab={setActiveTab} />
          {isAdmin && (
            <TabButton icon={Percent} label="Fees" tabName="fees" activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
          {(market.status === 'active' || market.status === 'ended' || market.status === 'expired') && (
             <>
               <TabButton icon={CheckSquare} label="Resolve" tabName="resolve" activeTab={activeTab} setActiveTab={setActiveTab} />
             </>
          )}
          {(['active', 'ended', 'expired', 'cancelled'].includes(market.status?.toLowerCase() || '')) && (
            <TabButton icon={XSquare} label={market.status === 'cancelled' ? 'Cancelled' : 'Cancel'} tabName="cancel" activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
          {(market.is_verified === false && market.bet_count === 0) && (
            <TabButton icon={Trash2} label="Delete" tabName="delete" activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
          {market.status === 'cancelled' && (
            <TabButton icon={DollarSign} label="Refunds" tabName="refunds" activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {renderTabContent()}
      </div>
    </div>
  );
}

// Helper Components (can be moved to separate files)
const TabButton = ({ icon: Icon, label, tabName, activeTab, setActiveTab }: { icon: React.ElementType, label: string, tabName: Tab, activeTab: Tab, setActiveTab: (tab: Tab) => void }) => {
  const isActive = activeTab === tabName;
  return (
    <button
      onClick={() => setActiveTab(tabName)}
      className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-base flex items-center space-x-2 transition-colors ${
        isActive
          ? 'border-wayja-teal text-wayja-teal'
          : 'border-transparent text-gray-500 hover:text-gray-900'
      }`}
      aria-current={isActive ? 'page' : undefined}
    >
      <Icon className="h-4 w-4 mr-2" />
      <span>{label}</span>
    </button>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-wayja-teal"></div>
  </div>
);

const ErrorMessage = ({ error, onRetry, retryLabel }: { error: string, onRetry?: () => void, retryLabel?: string }) => (
  <div className="flex flex-col items-center justify-center h-64 p-4">
    <div className="bg-red-50 p-6 rounded-lg shadow-md max-w-md text-center">
      <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
      <p className="text-red-700 mb-6">{error}</p>
      {onRetry && retryLabel && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center mx-auto"
        >
          {retryLabel === 'Back to Markets' && <ArrowLeft className="w-4 h-4 mr-2" />}
          {retryLabel}
        </button>
      )}
    </div>
  </div>
);

const InfoMessage = ({ message }: { message: string }) => (
   <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
     <div className="flex items-center">
       <Info className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
       <p className="text-sm text-blue-700">{message}</p>
     </div>
   </div>
 );

// Helper to get status color (Tailwind example)
const getStatusColor = (status: string | undefined): string => { // Added undefined check
  switch (status?.toLowerCase()) { // Optional chaining
    case 'active': return 'bg-green-100 text-green-800';
    case 'resolved': return 'bg-blue-100 text-blue-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    case 'ended': return 'bg-orange-100 text-orange-800';
    case 'expired': return 'bg-gray-100 text-gray-700';
    case 'pending_approval': return 'bg-yellow-100 text-yellow-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Helper to get status icon and text
const StatusBadge = ({ status }: { status: string | undefined }) => {
  if (!status) return null;
  
  const statusLower = status.toLowerCase();
  let icon = null;
  let text = status;
  
  // Format display text
  if (statusLower === 'pending_approval') text = 'Pending';
  
  // Add dot indicator for active status
  if (statusLower === 'active') {
    icon = <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></span>;
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
      {icon}
      {text}
    </span>
  );
};

// Add MarketDeleter component
const MarketDeleter = ({ market, marketId, onDeleted }: { market: Market, marketId: string, onDeleted: () => void }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Check if market is eligible for deletion
  const canDelete = market.is_verified === false && market.bet_count === 0;

  const handleDelete = async () => {
    if (!canDelete) {
      setError("This market cannot be deleted. Only unverified markets with no trades can be deleted.");
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      logger.info(`Starting deletion process for market ID: ${marketId}`);

      // Call the delete_market function
      const { error: deleteError } = await supabase.rpc('delete_market', {
        p_market_id: marketId
      });

      if (deleteError) {
        logger.error('Error deleting market:', deleteError);
        throw new Error(deleteError.message);
      }

      logger.info('Market successfully deleted');
      
      // Call the onDeleted callback which handles navigation
      onDeleted();
    } catch (err) {
      logger.error('Error in deletion process:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete market');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!canDelete) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="mb-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Market Cannot Be Deleted</h2>
          <p className="text-gray-600">
            This market cannot be deleted. Only unverified markets with no trades can be deleted.
          </p>
          {market.is_verified && (
            <p className="mt-2 text-red-600">Market is verified</p>
          )}
          {(market.bet_count && market.bet_count > 0) && (
            <p className="mt-2 text-red-600">Market has {market.bet_count} trades</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <p className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </p>
        </div>
      )}
      
      <div className="mb-6 text-center">
        <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Market</h2>
        <p className="text-gray-600">
          Are you sure you want to delete this market? This action cannot be undone.
        </p>
        
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-left mb-6">
          <h3 className="font-medium text-yellow-800 mb-2">Market Information</h3>
          <p className="text-yellow-700 text-sm mb-1"><span className="font-medium">Title:</span> {market.title}</p>
          <p className="text-yellow-700 text-sm mb-1"><span className="font-medium">Status:</span> {market.status}</p>
          <p className="text-yellow-700 text-sm mb-1"><span className="font-medium">Verified:</span> {market.is_verified ? 'Yes' : 'No'}</p>
          <p className="text-yellow-700 text-sm"><span className="font-medium">Trade Count:</span> {market.bet_count || 0}</p>
        </div>
      </div>
      
      {!showConfirm ? (
        <div className="flex justify-center">
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Delete Market
          </button>
        </div>
      ) : (
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setShowConfirm(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </>
            ) : (
              'Confirm Delete'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// Add RefundsTab component
const RefundsTab = ({ market, marketId }: { market: Market, marketId: string }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingBets, setIsLoadingBets] = useState(false);
  const [refundCount, setRefundCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelledBets, setCancelledBets] = useState<BetWithRelations[]>([]);
  const [refundedBets, setRefundedBets] = useState<BetWithRelations[]>([]);
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  // Fetch all bets for this market
  const fetchBets = useCallback(async () => {
    if (!marketId) return;
    
    try {
      setIsLoadingBets(true);
      setError(null);
      
      // Fetch cancelled bets using the database function
      const { data: cancelledData, error: cancelledError } = await supabase
        .rpc('get_cancelled_bets', { p_market_id: marketId });
        
      if (cancelledError) {
        logger.error('Error fetching cancelled bets:', cancelledError);
        throw cancelledError;
      }
      
      // Fetch refunded bets using the database function
      const { data: refundedData, error: refundedError } = await supabase
        .rpc('get_refunded_bets', { p_market_id: marketId });
        
      if (refundedError) {
        logger.error('Error fetching refunded bets:', refundedError);
        throw refundedError;
      }
      
      // Transform the data to match our interface
      const transformedCancelledData = (cancelledData || []).map((bet: any) => ({
        ...bet,
        users: { email: bet.user_email },
        market_options: { name: bet.option_name },
      }));
      
      const transformedRefundedData = (refundedData || []).map((bet: any) => ({
        ...bet,
        users: { email: bet.user_email },
        market_options: { name: bet.option_name },
      }));
      
      setCancelledBets(transformedCancelledData);
      setRefundedBets(transformedRefundedData);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError('Failed to fetch bets: ' + errorMessage);
    } finally {
      setIsLoadingBets(false);
    }
  }, [marketId]);

  // Load bets on component mount
  useEffect(() => {
    fetchBets();
  }, [fetchBets]);

  const handleProcessRefunds = async () => {
    if (!marketId) {
      setError('Market ID is required');
      return;
    }

    // Only admins can process refunds
    if (!isAdmin) {
      setError('Only administrators can process refunds.');
      return;
    }
    
    try {
      setIsProcessing(true);
      setError(null);
      
      // Call the process_market_cancellation_refunds RPC function
      const { data, error: rpcError } = await supabase.rpc('process_market_cancellation_refunds', { 
        p_market_id: marketId
      });
      
      if (rpcError) {
        logger.error('Error processing refunds:', rpcError);
        throw rpcError;
      }
      
      setRefundCount(data);
      // Refresh the bet lists
      await fetchBets();
      
    } catch (error: unknown) {
      logger.error('Error processing refunds:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError('Failed to process refunds: ' + errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };


  const renderBetTable = (bets: BetWithRelations[], title: string, emptyMessage: string) => {
    if (isLoadingBets) {
      return (
        <div className="flex items-center justify-center p-6">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Loading trades...</span>
        </div>
      );
    }

    if (bets.length === 0) {
      return (
        <div className="bg-gray-50 p-4 rounded-md text-gray-500 text-center">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div>
        <h3 className="text-lg font-medium mb-3">{title} ({bets.length})</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trade ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Option</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Service Fee</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Refund</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bets.map(bet => {
                const serviceFee = bet.amount - (bet.amount_after_fee || 0);
                const totalRefund = bet.amount;
                
                return (
                  <tr key={bet.id}>
                    <td className="px-4 py-2 text-sm">{bet.id.substring(0, 8)}...</td>
                    <td className="px-4 py-2 text-sm">{bet.users?.email || 'Unknown'}</td>
                    <td className="px-4 py-2 text-sm">{bet.market_options?.name || 'Unknown'}</td>
                    <td className="px-4 py-2 text-sm">{formatCurrency(bet.amount_after_fee || 0)}</td>
                    <td className="px-4 py-2 text-sm">{formatCurrency(serviceFee)}</td>
                    <td className="px-4 py-2 text-sm font-medium">{formatCurrency(totalRefund)}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        bet.status === 'refunded' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {bet.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-medium">
              <tr>
                <td colSpan={3} className="px-4 py-2 text-right">Totals:</td>
                <td className="px-4 py-2">{formatCurrency(bets.reduce((sum, bet) => sum + (bet.amount_after_fee || 0), 0))}</td>
                <td className="px-4 py-2">{formatCurrency(bets.reduce((sum, bet) => sum + (bet.amount - (bet.amount_after_fee || 0)), 0))}</td>
                <td className="px-4 py-2">{formatCurrency(bets.reduce((sum, bet) => sum + bet.amount, 0))}</td>
                <td className="px-4 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4 text-gray-900">Process Refunds</h2>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <p className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </p>
        </div>
      )}
      
      {refundCount !== null && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700 flex items-center">
            <CheckSquare className="w-5 h-5 mr-2" />
            Successfully processed refunds for {refundCount} bets.
          </p>
        </div>
      )}
      
      {market.status === 'cancelled' ? (
        <div className="space-y-8">
          {cancelledBets.length > 0 && (
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                This market has been cancelled. You can now process refunds for all trades.
              </p>
              
              {isAdmin && cancelledBets.length > 0 && (
                <button 
                  className="px-4 py-2 mb-6 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center"
                  onClick={handleProcessRefunds}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Process Refunds for {cancelledBets.length} Bets
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          
          <div className="space-y-8">
            {/* Bets waiting to be refunded */}
            {renderBetTable(
              cancelledBets,
              "Trades Waiting for Refund",
              "No trades waiting for refund."
            )}
            
            {/* Already refunded bets */}
            {renderBetTable(
              refundedBets,
              "Refunded Trades",
              "No trades have been refunded yet."
            )}
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-yellow-700 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            This market has not been cancelled yet. You need to cancel it before processing refunds.
          </p>
        </div>
      )}
    </div>
  );
};
